import { useParams } from "react-router-dom";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  addTagsToProspects,
  deleteTagsToProspects,
} from "../../../../api/PRMAPI";
import { Button, SegmentedControl, Stack } from "@mantine/core";
import { IconChevronRight, IconTags } from "@tabler/icons-react";
import { useProfileStore } from "../../../../stores/UserStore";
import { deselectAll } from "../utils";
import TagsRessourceSelector from "../../../../components/Selectors/TagsRessourceSelector";
import PopOver from "../../../../components/View/PopOver";
import { transformFilterToServer } from "../../../../functions/utilFunctions";

const defaults = ["opener", "clicker", "answerer", "to_call", "out_of_office"];

const LeadsBulkTags = ({
  handleClose,
  tableFilter,
  tableSelection,
  handleFinish,
}) => {
  const lang = useLanguage((s) => s.language);
  const query = useLeadsStore((s) => s.query);

  const globalFilter = useLeadsStore((s) => s.filter);
  const globalSelectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);

  const selectedIds = tableSelection || globalSelectedIds;
  const filter = tableFilter
    ? transformFilterToServer(tableFilter)
    : globalFilter;

  const containers = useLeadsContainerStore((s) => s.containers);
  const setContainers = useLeadsContainerStore((s) => s.setContainers);
  const setReloadAll = useLeadsContainerStore((s) => s.setReloadAll);

  const { userId } = useParams();
  const [functionType, setFunctionType] = useState("add");
  const [loading, setLoading] = useState(false);
  const [isSelection, setIsSelection] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isAll, setIsAll] = useState(false);
  const [tags, setTags] = useState([]);

  const handleSelectFinish = () => {
    let selectedIdsColumns = Object.keys(selectedIds);
    let newContainers = [...containers];
    selectedIdsColumns.forEach((c) => {
      if (selectedIds[c]?.length > 0) {
        let i = newContainers.findIndex(
          (x) => x.id.toString() === c.toString(),
        );
        if (i > -1) newContainers[i].loading = true;
      }
    });

    selectedColumns.forEach((s) => {
      let i = newContainers.findIndex((x) => x.id.toString() === s.toString());
      if (i > -1) newContainers[i].loading = true;
    });

    setContainers(newContainers);
    return deselectAll();
  };

  const handleSubmit = (type, closePopover) => {
    setLoading(true);
    setIsSelection(type === 1);
    setIsAll(type === 0);
    setIsFilter(type === "filter");

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (query) {
            v.push({
              field_name: "any_datafield",
              type: "contains",
              value: query,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    let justFilter = {
      mode: "and",
      values: [],
    };
    if (type === "filter" && filter) {
      justFilter.values.push(filter);
    }
    if (type === "filter" && query) {
      justFilter.values.push({
        field_name: "any_datafield",
        type: "contains",
        value: query,
      });
    }

    const func =
      functionType === "add" ? addTagsToProspects : deleteTagsToProspects;
    func({
      user_id: userId
        ? parseInt(userId)
        : useProfileStore.getState().profile.id,
      filter: type === "filter" ? justFilter : parsedFilter,
      contact_ids:
        type === 0 ? [] : Object.values(selectedIds).flatMap((x) => x),
      excluded_contact_ids: type === 0 ? [] : excluded,
      tag_ids: tags.map((x) => x.id),
    })
      .then((response) => {
        if (type === 1) {
          handleSelectFinish();
        } else {
          setReloadAll(true);
        }
        handleClose?.();
        handleFinish?.();
        closePopover();
      })
      .finally(() => {
        setLoading(false);
        setIsAll(false);
        setIsSelection(false);
        setIsFilter(false);
      });
  };

  const canExport = () => {
    if (Object.values(selectedIds).flatMap((x) => x).length > 0) {
      return true;
    }
    if (selectedColumns.length > 0) {
      return true;
    }

    return false;
  };

  return (
    <PopOver
      opener={(handleOpen, ref) => (
        <Button
          variant="light"
          justify="start"
          leftSection={<IconTags size={16} />}
          rightSection={<IconChevronRight size={16} />}
          onClick={handleOpen}
          ref={ref}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
        >
          {lang.tags.browser_tab_title}
        </Button>
      )}
      popoverChildren={(closePopover) => (
        <Stack gap={"xs"} w={300}>
          <SegmentedControl
            data={[
              {
                label: lang.targeting_linkedin_google.add,
                value: "add",
              },
              {
                label: lang.contact_lists.delete_contacts.label,
                value: "delete",
              },
            ]}
            value={functionType}
            onChange={setFunctionType}
          />
          <TagsRessourceSelector value={tags} setValue={setTags} />

          <Button
            loading={isSelection}
            disabled={!canExport() || loading}
            onClick={() => handleSubmit(1, closePopover)}
          >
            {lang.contact_lists.export_contacts.selection}
          </Button>

          <Button
            loading={isFilter}
            disabled={
              loading || ((!filter || !filter?.values?.length === 0) && !query)
            }
            onClick={() => handleSubmit("filter", closePopover)}
          >
            {lang.contact_lists.export_contacts.filter}
          </Button>

          {/* <Button
            loading={isAll}
            disabled={loading}
            onClick={() => handleSubmit(0)}
            variant="light"
          >
            {lang.contact_lists.tabs_title.all_contacts}
          </Button> */}
        </Stack>
      )}
    />
  );
};

export default LeadsBulkTags;
